// Minimal HLS media-playlist parser for client-side downloads.
//
// This is intentionally NOT a general-purpose HLS parser — it only extracts
// what `downloadHlsLevel` needs to stitch a single rendition into one file:
// the ordered segment list (with byte ranges + sequence numbers), an optional
// fMP4 init segment, and a single AES-128 key. hls.js already owns live
// playback; this runs only on an explicit "Download" click against a VOD
// media playlist whose URL we got from `hls.levels[i].uri`.

export interface M3u8ByteRange {
  length: number;
  offset: number;
}

export interface M3u8Segment {
  url: string;
  byteRange?: M3u8ByteRange;
  /** Media sequence number — used to derive the AES-128 IV when none is given. */
  seq: number;
}

export interface M3u8Key {
  method: 'AES-128' | 'SAMPLE-AES';
  uri: string;
  /** Hex IV string (e.g. "0x00..ff") when the playlist pins one. */
  iv?: string;
}

export interface ParsedPlaylist {
  /** Present for fMP4 renditions (#EXT-X-MAP). */
  initSegmentUrl?: string;
  initByteRange?: M3u8ByteRange;
  segments: M3u8Segment[];
  key?: M3u8Key;
  /** True when the rendition is fragmented MP4 (has an init segment). */
  isFmp4: boolean;
  /**
   * Set to a human-readable reason when the playlist cannot be downloaded
   * client-side (e.g. SAMPLE-AES). Callers should fail fast on this.
   */
  unsupported?: string;
}

/** Parse `#EXT-X-...:k=v,k="v",...` attribute lists into a flat record. */
function parseAttrs(input: string): Record<string, string> {
  const attrs: Record<string, string> = {};
  // Split on commas that are not inside double quotes.
  const parts = input.match(/(?:[^,"]+|"[^"]*")+/g) ?? [];
  for (const part of parts) {
    const eq = part.indexOf('=');
    if (eq === -1) continue;
    const key = part.slice(0, eq).trim();
    let value = part.slice(eq + 1).trim();
    if (value.startsWith('"') && value.endsWith('"')) {
      value = value.slice(1, -1);
    }
    attrs[key] = value;
  }
  return attrs;
}

/** Parse a `<length>[@<offset>]` byte-range, defaulting the offset to `prevEnd`. */
function parseByteRange(raw: string, prevEnd: number): M3u8ByteRange {
  const [lenStr, offStr] = raw.trim().split('@');
  const length = Number(lenStr);
  const offset = offStr != null ? Number(offStr) : prevEnd;
  return { length, offset };
}

export function parseM3u8(text: string, baseUrl: string): ParsedPlaylist {
  const segments: M3u8Segment[] = [];
  let initSegmentUrl: string | undefined;
  let initByteRange: M3u8ByteRange | undefined;
  let key: M3u8Key | undefined;
  let unsupported: string | undefined;

  let mediaSequence = 0;
  let pendingByteRange: M3u8ByteRange | undefined;
  // Per-resource running end so an @-less BYTERANGE continues the previous one.
  const lastEndByUrl = new Map<string, number>();

  const lines = text.split(/\r?\n/);
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (line === '') continue;

    if (line.startsWith('#')) {
      if (line.startsWith('#EXT-X-MEDIA-SEQUENCE:')) {
        mediaSequence = Number(line.slice('#EXT-X-MEDIA-SEQUENCE:'.length).trim()) || 0;
      } else if (line.startsWith('#EXT-X-MAP:')) {
        const attrs = parseAttrs(line.slice('#EXT-X-MAP:'.length));
        if (attrs.URI) initSegmentUrl = new URL(attrs.URI, baseUrl).toString();
        if (attrs.BYTERANGE) initByteRange = parseByteRange(attrs.BYTERANGE, 0);
      } else if (line.startsWith('#EXT-X-KEY:')) {
        const attrs = parseAttrs(line.slice('#EXT-X-KEY:'.length));
        const method = attrs.METHOD;
        if (method === 'NONE' || method == null) {
          key = undefined;
        } else if (method === 'AES-128') {
          key = {
            method: 'AES-128',
            uri: attrs.URI ? new URL(attrs.URI, baseUrl).toString() : '',
            ...(attrs.IV ? { iv: attrs.IV } : {}),
          };
        } else if (method === 'SAMPLE-AES') {
          unsupported = 'SAMPLE-AES encryption is not supported for download';
          key = {
            method: 'SAMPLE-AES',
            uri: attrs.URI ? new URL(attrs.URI, baseUrl).toString() : '',
            ...(attrs.IV ? { iv: attrs.IV } : {}),
          };
        } else {
          unsupported = `Unsupported EXT-X-KEY METHOD=${method}`;
        }
      } else if (line.startsWith('#EXT-X-BYTERANGE:')) {
        // Resolved once we see the following URI (need the resource to know prevEnd).
        pendingByteRange = { length: Number(line.slice('#EXT-X-BYTERANGE:'.length).split('@')[0]), offset: NaN };
        const offPart = line.slice('#EXT-X-BYTERANGE:'.length).split('@')[1];
        if (offPart != null) pendingByteRange.offset = Number(offPart);
      }
      // All other tags (#EXTINF, #EXT-X-VERSION, comments, #EXT-X-ENDLIST, …) are ignored.
      continue;
    }

    // A non-tag, non-blank line is a segment URI.
    const url = new URL(line, baseUrl).toString();
    let byteRange: M3u8ByteRange | undefined;
    if (pendingByteRange) {
      const prevEnd = lastEndByUrl.get(url) ?? 0;
      const offset = Number.isNaN(pendingByteRange.offset) ? prevEnd : pendingByteRange.offset;
      byteRange = { length: pendingByteRange.length, offset };
      lastEndByUrl.set(url, offset + pendingByteRange.length);
      pendingByteRange = undefined;
    }
    segments.push({ url, seq: mediaSequence + segments.length, ...(byteRange ? { byteRange } : {}) });
  }

  return {
    segments,
    isFmp4: initSegmentUrl != null,
    ...(initSegmentUrl ? { initSegmentUrl } : {}),
    ...(initByteRange ? { initByteRange } : {}),
    ...(key ? { key } : {}),
    ...(unsupported ? { unsupported } : {}),
  };
}
